import type { IMaybeAsync } from '~/helpers/types';
import type { IStorage } from './drivers';
import { STORAGE_KEY_PREFIX } from './constants';

export type IKeyManifestEntry = {
  keys: Array<string>;
};

/**
 * Used when the  driver has no `allKeys()` implementation.
 */
export class KeyManifest {
  private readonly manifestKey: string;

  public constructor(
    private storage: IStorage,
    private clientId: string,
  ) {
    this.manifestKey = this.createManifestKeyFrom(this.clientId);
  }

  private createManifestKeyFrom(clientId: string): string {
    return `${STORAGE_KEY_PREFIX}::${clientId}`;
  }

  public async add(key: string): Promise<void> {
    const keys = new Set(
      this.storage.get<IKeyManifestEntry>(this.manifestKey)?.keys ?? [],
    );

    keys.add(key);

    await this.storage.set(this.manifestKey, {
      keys: [...keys],
    });
  }

  public all(): IMaybeAsync<IKeyManifestEntry | undefined> {
    return this.storage.get<IKeyManifestEntry>(this.manifestKey);
  }

  public async remove(key: string): Promise<void> {
    const entry = this.storage.get<IKeyManifestEntry>(this.manifestKey);

    if (entry) {
      const keys = new Set(entry.keys);
      keys.delete(key);

      if (keys.size > 0) {
        return await this.storage.set(this.manifestKey, { keys: [...keys] });
      }

      return this.storage.delete(this.manifestKey);
    }
  }

  public clear(): IMaybeAsync<void> {
    return this.storage.delete(this.manifestKey);
  }
}
